import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Select,
    MenuItem,
    InputAdornment,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Clear as ClearIcon,
    KeyboardArrowDown as ChevronDownIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    Check as CheckIcon
} from '@mui/icons-material';
import WidgetWraper from 'components/Common/WidgetWraper';
import { SKY_BLUE, SOFT_RED, MUTED_TEXT } from './constants';
import { dropdownSx, iconBtnSx, cardTitleSx, searchFieldSx, tableHeaderSx } from './styles';

/**
 * Single Row for the Basic Filters Section
 */
export const FilterRow: React.FC<{
    fieldOptions: string[];
    valueOptions: string[];
    field: string;
    value: string;
    onDelete: () => void;
    onAdd: () => void;
    onFieldChange: (val: string) => void;
    onValueChange: (val: string) => void;
}> = ({ fieldOptions, valueOptions, field, value, onDelete, onAdd, onFieldChange, onValueChange }) => {
    return (
        <Box display="flex" alignItems="center" gap={4} mb={1.5} px={2}>
            <Box display="flex" sx={{ gap: '10px' }} width="120px">
                <IconButton sx={iconBtnSx(SOFT_RED)} onClick={onDelete}>
                    <CloseIcon />
                </IconButton>
                <IconButton sx={iconBtnSx(SKY_BLUE)} onClick={onAdd}>
                    <AddIcon />
                </IconButton>
            </Box>

            <Box flex={1}>
                <Select
                    fullWidth
                    value={field}
                    onChange={(e) => onFieldChange(e.target.value)}
                    sx={{
                        ...dropdownSx,
                        '& .MuiSelect-icon': {
                            color: SKY_BLUE,
                            fontSize: '24px',
                            right: '12px',
                        }
                    }}
                    renderValue={(selected) => selected as string}
                    IconComponent={ChevronDownIcon}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                borderRadius: '4px',
                                mt: 0.5,
                                backgroundColor: '#FFFFFF !important',
                                color: '#444 !important',
                                boxShadow: '0px 8px 32px rgba(0,0,0,0.12)',
                                border: '1px solid #E0E4EC !important',
                                '& .MuiList-root': { py: 0 }
                            }
                        }
                    }}
                >
                    {fieldOptions.map(opt => {
                        const isSelected = field === opt;
                        return (
                            <MenuItem
                                key={opt}
                                value={opt}
                                sx={{
                                    fontSize: '14px',
                                    fontFamily: 'Montserrat',
                                    fontWeight: isSelected ? 500 : 400,
                                    py: 1.25,
                                    px: 2,
                                    backgroundColor: isSelected ? '#39ACE733 !important' : 'transparent',
                                    color: '#444 !important',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    '&:hover': {
                                        backgroundColor: '#39ACE733 !important',
                                    }
                                }}
                            >
                                <Typography sx={{ fontSize: '14px', fontFamily: 'Montserrat', fontWeight: 'inherit' }}>{opt}</Typography>
                                {isSelected && (
                                    <CheckIcon sx={{ fontSize: '18px', color: '#1A5A96' }} />
                                )}
                            </MenuItem>
                        );
                    })}
                </Select>
            </Box>

            <Box flex={1}>
                <Select
                    fullWidth
                    value={value}
                    onChange={(e) => onValueChange(e.target.value)}
                    sx={{
                        ...dropdownSx,
                        '& .MuiSelect-icon': {
                            color: MUTED_TEXT,
                            fontSize: '24px',
                            right: '12px',
                        }
                    }}
                    renderValue={(selected) => selected as string}
                    IconComponent={ChevronDownIcon}
                    disabled={!field}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                borderRadius: '4px',
                                mt: 0.5,
                                backgroundColor: '#FFFFFF !important',
                                color: '#444 !important',
                                boxShadow: '0px 8px 32px rgba(0,0,0,0.12)',
                                border: '1px solid #E0E4EC !important',
                                '& .MuiList-root': { py: 0 }
                            }
                        }
                    }}
                >
                    {valueOptions.map(opt => {
                        const isSelected = value === opt;
                        return (
                            <MenuItem
                                key={opt}
                                value={opt}
                                sx={{
                                    fontSize: '14px',
                                    fontFamily: 'Montserrat',
                                    fontWeight: isSelected ? 500 : 400,
                                    py: 1.25,
                                    px: 2,
                                    backgroundColor: isSelected ? '#39ACE733 !important' : 'transparent',
                                    color: '#444 !important',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    '&:hover': {
                                        backgroundColor: '#39ACE733 !important',
                                    }
                                }}
                            >
                                <Typography sx={{ fontSize: '14px', fontFamily: 'Montserrat', fontWeight: 'inherit' }}>{opt}</Typography>
                                {isSelected && (
                                    <CheckIcon sx={{ fontSize: '18px', color: '#1A5A96' }} />
                                )}
                            </MenuItem>
                        );
                    })}
                </Select>
            </Box>
        </Box>
    );
};

/**
 * Main Filters Section
 */
export const FiltersCard: React.FC = () => {
    const [rows, setRows] = useState([
        { id: 1, field: 'Sectors', value: 'PBNA' },
        { id: 2, field: 'Programs', value: 'Program 1' }
    ]);

    const handleAdd = () => {
        setRows([...rows, { id: Date.now(), field: '', value: '' }]);
    };

    const handleDelete = (id: number) => {
        if (rows.length > 1) {
            setRows(rows.filter(r => r.id !== id));
        }
    };

    const getValueOptions = (field: string) => {
        switch (field) {
            case 'Sectors':
                return ['PBNA', 'PFNA', 'AMESA', 'APAC', 'LATAM', 'EUROPE'];
            case 'Programs':
                return ['Program 1', 'Program 2', 'Program 3', 'Program 4'];
            case 'Business Units':
                return ['BU 1', 'BU 2', 'BU 3'];
            case 'Capabilities':
                return ['Capability 1', 'Capability 2', 'Capability 3'];
            default:
                return [];
        }
    };

    return (
        <WidgetWraper sx={{ border: '1px solid #E0E4EC', backgroundColor: '#fff', p: 0, borderRadius: '4px' }}>
            <Box display="flex" alignItems="center" sx={{ gap: '24px', py: '12px', pl: '40px', pr: '16px' }}>
                <Typography sx={{ ...cardTitleSx, fontSize: '16px', color: '#000' }}>Filters</Typography>
                <TextField
                    placeholder="Search"
                    variant="outlined"
                    sx={{ ...searchFieldSx, width: '400px' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: MUTED_TEXT, fontSize: '20px' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box flexGrow={1} />
                <ChevronDownIcon sx={{ color: SKY_BLUE, cursor: 'pointer' }} />
            </Box>

            <Divider sx={{ mx: 2, mb: 1.5 }} />

            <Box display="flex" alignItems="center" gap={4} mb={1} px={2}>
                <Box width="120px">
                    <Typography
                        sx={{
                            fontFamily: 'Montserrat',
                            fontSize: '13px',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            color: '#A7ADCB',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                        }}
                        onClick={() => setRows([{ id: 1, field: '', value: '' }])}
                    >
                        <ClearIcon sx={{ fontSize: '16px', mr: 0.5, color: '#A7ADCB' }} /> CLEAR ALL
                    </Typography>
                </Box>
                <Box flex={1}>
                    <Typography sx={{ ...tableHeaderSx, mb: 0 }}>Field</Typography>
                </Box>
                <Box flex={1}>
                    <Typography sx={{ ...tableHeaderSx, mb: 0 }}>Value</Typography>
                </Box>
            </Box>

            {rows.map((row) => (
                <FilterRow
                    key={row.id}
                    field={row.field}
                    value={row.value}
                    fieldOptions={['Sectors', 'Programs', 'Business Units', 'Capabilities']}
                    valueOptions={getValueOptions(row.field)}
                    onDelete={() => handleDelete(row.id)}
                    onAdd={handleAdd}
                    onFieldChange={(val) => {
                        setRows(rows.map(r => r.id === row.id ? { ...r, field: val, value: '' } : r));
                    }}
                    onValueChange={(val) => {
                        setRows(rows.map(r => r.id === row.id ? { ...r, value: val } : r));
                    }}
                />
            ))}

            <Divider sx={{ mx: 2, mt: 1 }} />

            <Box display="flex" justifyContent="flex-end" sx={{ py: 1, px: 2 }}>
                <Button
                    variant="outlined"
                    sx={{
                        borderColor: SKY_BLUE,
                        color: SKY_BLUE,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        px: 3,
                        borderRadius: '4px',
                        '&:hover': {
                            borderColor: '#39ACE7',
                            backgroundColor: 'rgba(59, 163, 224, 0.04)'
                        }
                    }}
                >
                    RUN QUERY
                </Button>
            </Box>
        </WidgetWraper>
    );
};

export default FiltersCard;
